import {
  AuiIf,
  ComposerPrimitive,
  useAui,
  useAuiState,
} from '@assistant-ui/react';
import {IconButton, type IconName, TagGroup, truncate} from '@wandb/components';
import React, {useEffect, useRef, useState} from 'react';

import {useDebounceState} from '../../util/hooks';
import {useNavContext} from '../NavContextProvider';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {useWBAgentContextStore} from './WBAgentContextStore';
import {WBAgentRunMentionDropdown} from './WBAgentRunMentionDropdown';

const ComposerAction = ({hasProjectContext}: {hasProjectContext: boolean}) => {
  const isEmpty = useAuiState(s => s.composer.isEmpty);

  return (
    <div className="flex">
      <AuiIf condition={s => !s.thread.isRunning}>
        <ComposerPrimitive.Send asChild>
          <IconButton
            isDisabled={isEmpty || !hasProjectContext}
            aria-label="send message"
            variant="primary"
            icon="up"
          />
        </ComposerPrimitive.Send>
      </AuiIf>
      <AuiIf condition={s => s.thread.isRunning}>
        <ComposerPrimitive.Cancel asChild>
          <IconButton aria-label="cancel query" icon="stop" />
        </ComposerPrimitive.Cancel>
      </AuiIf>
    </div>
  );
};

export const WBAgentComposer = () => {
  const aui = useAui();
  const composer = aui.composer();
  const {params} = useNavContext();
  const nightModeRevertFilter = useNightModeRevertFilter();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [_liveQuery, debouncedMentionQuery, setMentionQuery] = useDebounceState(
    '',
    300
  );
  const [mentionIndices, setMentionIndices] = useState<{
    start: number;
    end: number;
  } | null>(null);

  const selectedRuns = useWBAgentContextStore(s => s.selectedRuns);
  const removeRun = useWBAgentContextStore(s => s.removeRun);
  const syncProject = useWBAgentContextStore(s => s.syncProject);

  // Clear runs context if user changes projects
  useEffect(() => {
    syncProject(params.projectName);
  }, [params.projectName, syncProject]);

  const handleComposerMention = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPosition = e.target.selectionStart ?? 0;
    const textBeforeCursor = value.slice(0, cursorPosition);
    const atMatch = textBeforeCursor.match(/@([\w-]*)$/);

    if (atMatch) {
      setIsMentionOpen(true);
      setMentionQuery(atMatch[1]);
      setMentionIndices({start: atMatch.index!, end: cursorPosition});
    } else {
      setIsMentionOpen(false);
      setMentionQuery('');
      setMentionIndices(null);
    }
  };

  const handleRunSelect = (displayName: string) => {
    const currentText = composer.getState().text;
    if (mentionIndices) {
      const before = currentText.slice(0, mentionIndices.start);
      const after = currentText.slice(mentionIndices.end);
      composer.setText(`${before}\`${displayName}\`${after}`);
    }

    setIsMentionOpen(false);
    setMentionQuery('');
    setMentionIndices(null);
    inputRef.current?.focus();
  };

  const injectContext = () => {
    const userText = composer.getState().text;
    if (selectedRuns.length > 0) {
      const contextBlock = selectedRuns
        .map(run => `{run_id: "${run.name}"}`)
        .join('\n');
      composer.setText(
        `<PromptContext> The following data is provided as supplementary to the user prompt\n${contextBlock}\n</PromptContext>\n${userText}`
      );
    }
  };

  const handleComposerKeyDown = (e: React.KeyboardEvent) => {
    if (isMentionOpen) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setIsMentionOpen(false);
        setMentionQuery('');
        setMentionIndices(null);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
      }
    }
  };

  const truncProjectName = params.projectName
    ? truncate(params.projectName, {pos: 'end', maxChars: 30}).value
    : undefined;

  return (
    <div className="relative">
      <div
        className="absolute inset-0 rounded bg-white dark:bg-moon-900 border dark:border-[#363C44]"
        style={{filter: nightModeRevertFilter}}
      />
      <ComposerPrimitive.Root
        className="relative flex flex-col gap-8 rounded border border-moon-250 bg-white p-12"
        onSubmit={injectContext}>
        {params.projectName && (
          <TagGroup
            aria-label="project name tag"
            icon="tag"
            items={[
              {
                id: 'project name',
                icon: 'folder-project' as IconName,
                textValue: truncProjectName ?? params.projectName,
                isDisabled: true,
              },
              ...selectedRuns.map(run => ({
                id: run.name,
                icon: 'running-repeat' as IconName,
                textValue: truncate(run.displayName, {
                  pos: 'end' as const,
                  maxChars: 29,
                }).value,
                isDisabled: false,
              })),
            ]}
            onRemove={key => {
              removeRun(String(key));
            }}
          />
        )}
        <ComposerPrimitive.Input
          ref={inputRef}
          placeholder="Ask the W&B agent"
          className="w-full resize-none bg-transparent text-[15px] outline-none placeholder:text-moon-500"
          rows={1}
          autoFocus
          aria-label="Message input"
          onChange={e => {
            handleComposerMention(e);
          }}
          onKeyDown={handleComposerKeyDown}
        />
        {isMentionOpen && params.projectName && params.entityName && (
          <WBAgentRunMentionDropdown
            query={debouncedMentionQuery}
            entityName={params.entityName}
            projectName={params.projectName}
            onSelect={handleRunSelect}
          />
        )}
        <div className="flex w-full justify-end">
          <ComposerAction
            hasProjectContext={!!params.entityName && !!params.projectName}
          />
        </div>
      </ComposerPrimitive.Root>
    </div>
  );
};
